import TableCol from "@rio-cloud/rio-uikit/TableCol";
import TableHead from "@rio-cloud/rio-uikit/TableHead";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import ContentLoader from "@rio-cloud/rio-uikit/ContentLoader";
import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import SimpleTooltip from "@rio-cloud/rio-uikit/SimpleTooltip";
import isEmpty from "lodash/fp/isEmpty";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";

const SKELETON_ROWS = 10;
const DEFAULT_SKELETON_CELL_HEIGHT = 20;
const DEFAULT_PLACEHOLDER_ROWS = 10;
const DEFAULT_ACTION_HEAD_COUNT = 1;
const SKELETON_CELL_WIDTHS = ["70%", "85%", "55%", "78%", "62%", "90%", "48%", "72%"];

const VISIBLE_SORT_KEY: Record<string, string> = {
  sortableDate: "date",
  sortableAmount: "amount",
};

export const resolveDisplaySortKey = (sortKey: unknown) => {
  if (!sortKey) {
    return "";
  }
  const sortKeyString = typeof sortKey === "string" ? sortKey : String((sortKey as unknown[])[0]);
  return VISIBLE_SORT_KEY[sortKeyString] || sortKeyString;
};

export type SharedColumnDetailsMap = { [key: string]: { [key: string]: number } };

export type SharedColumnLabel = { [key: string]: string };

export type SharedVehicleRow<TStatus extends string> = {
  id: string;
  date: string;
  sortableDate?: string;
  amount: string;
  sortableAmount?: number;
  branch: string;
  segment: string | null;
  customer: string | null;
  vehicle: string | null;
  driver: string | null;
  driverIds: string[];
  category: string;
  subcategory: string;
  taxes: string;
  status?: TStatus;
  recurrenceId?: string | null;
  source?: string | null;
  integrationOrigin?: string | null;
  trip?: string | null;
  tripId?: string | null;
  tripDriverId?: string | null;
  paymentMethod?: string | null;
  deletable?: boolean;
  version?: number | null;
  amountValue?: number;
  installmentNumber?: number | null;
  totalInstallments?: number | null;
};

export type SharedTableSelectionState = "none" | "some" | "all";

export interface SharedTableSelection<T> {
  isSelected: (row: T) => boolean;
  isNotDeletable: (row: T) => boolean;
  onToggle: (row: T) => void;
  headerState: SharedTableSelectionState;
  headerDisabled?: boolean;
  headerHidden?: boolean;
  onToggleAll: () => void;
  getNotDeletableTooltip: (row: T) => string;
  notDeletableTooltipTitle: string;
  lockNotDeletable?: boolean;
}

export interface SharedTableBaseProps<TRow, TSortKey> {
  rows: TRow[];
  isViewTypeTable: boolean;
  tableRef: React.RefObject<HTMLTableElement>;
  tableClassNames: string;
  columns: string[];
  columnsDetails: SharedColumnDetailsMap;
  columnLabels: SharedColumnLabel;
  sortKey: TSortKey;
  sortDirection: SortDirectionType;
  handleSortChange: (event: React.MouseEvent<HTMLTableCellElement>) => void;
  onActiveRowChange: (event: React.MouseEvent<HTMLTableRowElement>) => void;
  isLoading?: boolean;
}

export interface SharedTableProps<T> {
  selection?: SharedTableSelection<T>;
  rows: T[];
  isViewTypeTable: boolean;
  tableRef: React.RefObject<HTMLTableElement>;
  tableClassNames: string;
  columns: string[];
  columnsDetails: SharedColumnDetailsMap;
  columnLabels: SharedColumnLabel;
  sortBy: string;
  sortDir?: SortDirectionType;
  handleSortChange: (event: React.MouseEvent<HTMLTableCellElement>) => void;
  onActiveRowChange: (event: React.MouseEvent<HTMLTableRowElement>) => void;
  notFoundMessage: string;
  containerClassName: string;
  containerStyle?: React.CSSProperties;
  renderCell: (row: T, column: string) => React.ReactNode;
  renderRowActions: (row: T) => React.ReactNode;
  renderColumnCol?: (column: string) => React.ReactNode;
  renderSkeletonActions?: () => React.ReactNode;
  getRowStyle?: (row: T) => React.CSSProperties | undefined;
  actionHeadCount?: number;
  skeletonCellHeight?: number;
  placeholderRows?: number;
  isLoading: boolean;
}

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

const SelectionCell = <T,>({ row, selection }: { row: T; selection: SharedTableSelection<T> }) => {
  const checkbox = <Checkbox checked={selection.isSelected(row)} onClick={() => selection.onToggle(row)} />;

  if (!selection.isNotDeletable(row)) {
    return (
      <td className="table-checkbox" onClick={stopPropagation}>
        {checkbox}
      </td>
    );
  }

  const tooltip = selection.getNotDeletableTooltip(row);
  return (
    <td className="table-checkbox" onClick={stopPropagation}>
      <SimpleTooltip
        content={
          <div className="display-flex flex-column gap-10">
            <span className="text-bold">{selection.notDeletableTooltipTitle}</span>
            <span>{tooltip}</span>
          </div>
        }
        placement="right"
        textAlignment="left"
      >
        {selection.lockNotDeletable ? (
          <span className="rioglyph rioglyph-lock text-size-16 text-color-gray" aria-label={tooltip} />
        ) : (
          <span className="display-inline-block" aria-label={tooltip}>
            {checkbox}
          </span>
        )}
      </SimpleTooltip>
    </td>
  );
};

const SharedTable = <T extends { id: string | number }>({
  rows,
  isViewTypeTable,
  tableRef,
  tableClassNames,
  columns,
  columnsDetails,
  columnLabels,
  sortBy,
  sortDir,
  handleSortChange,
  onActiveRowChange,
  notFoundMessage,
  containerClassName,
  containerStyle,
  renderCell,
  renderRowActions,
  renderColumnCol,
  renderSkeletonActions,
  getRowStyle,
  actionHeadCount = DEFAULT_ACTION_HEAD_COUNT,
  skeletonCellHeight = DEFAULT_SKELETON_CELL_HEIGHT,
  placeholderRows = DEFAULT_PLACEHOLDER_ROWS,
  isLoading,
  selection,
}: SharedTableProps<T>) => (
  <div className={containerClassName} style={containerStyle}>
    {!isLoading && isEmpty(rows) && !isViewTypeTable && (
      <NotFoundState message={notFoundMessage} outerClassName="bg-none" />
    )}
    <table ref={tableRef} className={tableClassNames}>
      <colgroup>
        {selection && <TableCol className="table-checkbox" />}
        {columns.map((column) =>
          renderColumnCol ? renderColumnCol(column) : <TableCol key={column} columnDetails={columnsDetails[column]} />
        )}
        <TableCol className="table-action" />
      </colgroup>
      <thead>
        <tr>
          {selection && (
            <th className="table-checkbox">
              {!selection.headerHidden && (
                <Checkbox
                  checked={selection.headerState === "all"}
                  indeterminate={selection.headerState === "some"}
                  disabled={selection.headerDisabled}
                  onClick={selection.onToggleAll}
                />
              )}
            </th>
          )}
          {columns.map((column) => (
            <TableHead
              key={column}
              label={columnLabels[column]}
              column={column}
              sortBy={sortBy}
              sortDir={sortDir}
              onClick={handleSortChange}
            />
          ))}
          {Array.from({ length: actionHeadCount }, (_, actionIndex) => (
            <TableHead key={`action-head-${actionIndex}`} className="table-action" />
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading &&
          Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
            <tr key={`skeleton-${rowIndex}`}>
              {selection && <td className="table-checkbox" />}
              {columns.map((col, colIndex) => (
                <td key={col} data-field={columnLabels[col]}>
                  <ContentLoader
                    className="display-block"
                    height={skeletonCellHeight}
                    width={SKELETON_CELL_WIDTHS[colIndex % SKELETON_CELL_WIDTHS.length]}
                  />
                </td>
              ))}
              {renderSkeletonActions ? renderSkeletonActions() : <td className="table-action" />}
            </tr>
          ))}
        {!isLoading && isEmpty(rows) && isViewTypeTable && (
          <tr className="bg-none">
            <td colSpan={columns.length + (selection ? 2 : 1)}>
              <NotFoundState outerClassName="border-none bg-none" message={notFoundMessage} />
            </td>
          </tr>
        )}
        {!isLoading &&
          rows.map((row) => (
            <tr
              key={row.id}
              data-id={row.id}
              onClick={onActiveRowChange}
              className={selection?.isSelected(row) ? "cursor-pointer bg-highlight-lightest" : "cursor-pointer"}
              style={getRowStyle?.(row)}
            >
              {selection && <SelectionCell row={row} selection={selection} />}
              {columns.map((col) => (
                <td key={col} data-field={columnLabels[col]}>
                  {renderCell(row, col)}
                </td>
              ))}
              {renderRowActions(row)}
            </tr>
          ))}
        {Array.from({ length: placeholderRows }, (_, placeholderIndex) => (
          <tr key={`placeholder-${placeholderIndex}`} className="table-card-placeholder" />
        ))}
      </tbody>
    </table>
  </div>
);

export default SharedTable;
